import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { maxQtyFor } from '../utils/stock';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Initialize cart from localStorage synchronously to avoid race conditions
const getInitialCart = () => {
  try {
    const savedCart = localStorage.getItem('hestia_cart');
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    localStorage.removeItem('hestia_cart');
  }
  return [];
};

export const CartProvider = ({ children }) => {
  // Initialize with localStorage value synchronously
  const [cart, setCart] = useState(getInitialCart);
  const isInitialized = useRef(true); // Already initialized from getInitialCart

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    // Always save since we initialized synchronously
    localStorage.setItem('hestia_cart', JSON.stringify(cart));
  }, [cart]);

  // Add item to cart
  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      // Check if product already exists in cart
      const existingItemIndex = prevCart.findIndex(item => item.id === product.id);
      
      const limit = maxQtyFor(product);
      let newCart;
      if (existingItemIndex > -1) {
        // Update quantity of existing item (never above the available stock)
        newCart = [...prevCart];
        newCart[existingItemIndex] = {
          ...newCart[existingItemIndex],
          stock: product.stock,
          quantity: Math.min(newCart[existingItemIndex].quantity + quantity, limit)
        };
      } else {
        // Add new item
        newCart = [...prevCart, { ...product, quantity: Math.min(quantity, limit) }];
      }
      // Sync save to localStorage immediately
      localStorage.setItem('hestia_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  // Update item quantity
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart => {
      const newCart = prevCart.map(item =>
        item.id === productId ? { ...item, quantity: Math.min(quantity, maxQtyFor(item)) } : item
      );
      localStorage.setItem('hestia_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Get cart total
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Get cart item count
  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
